/**
 * @license
 * Copyright 2026 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 *
 * g3-prettier-ignore-file
 */

/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { type Result } from "./fp.js";
export type APICall =
  | {
      status: "complete";
      request: Request;
      response: Response;
    }
  | {
      status: "request-error";
      request: Request;
      response?: undefined;
    }
  | {
      status: "invalid";
      request?: undefined;
      response?: undefined;
    };

export class APIPromise<T> implements Promise<T> {
  readonly _promise: Promise<[T, APICall]>;
  _unwrapped: Promise<T> | null;
  _callSource: Promise<APICall> | null;

  readonly [Symbol.toStringTag] = "APIPromise";

  constructor(
    p: [T, APICall] | Promise<[T, APICall]>,
    callSource?: Promise<APICall>,
  ) {
    this._promise = p instanceof Promise ? p : Promise.resolve(p);
    this._unwrapped = p instanceof Promise ? null : Promise.resolve(p[0]);
    this._callSource = callSource ?? null;
  }

  _getUnwrapped(): Promise<T> {
    return (this._unwrapped ??= this._promise.then(([value]) => value));
  }

  then<TResult1 = T, TResult2 = never>(
    onfulfilled?:
      | ((value: T) => TResult1 | PromiseLike<TResult1>)
      | null
      | undefined,
    onrejected?:
      | ((reason: any) => TResult2 | PromiseLike<TResult2>)
      | null
      | undefined,
  ): Promise<TResult1 | TResult2> {
    return this._promise.then(
      onfulfilled ? ([value]) => onfulfilled(value) : void 0,
      onrejected,
    );
  }

  catch<TResult = never>(
    onrejected?:
      | ((reason: any) => TResult | PromiseLike<TResult>)
      | null
      | undefined,
  ): Promise<T | TResult> {
    return this._getUnwrapped().catch(onrejected);
  }

  finally(onfinally?: (() => void) | null | undefined): Promise<T> {
    return this._getUnwrapped().finally(onfinally);
  }

  $inspect(): Promise<[T, APICall]> {
    return this._promise;
  }

  asResponse(): Promise<Response> {
    const src = (this._callSource ??= this._promise.then(
      ([, call]) => call,
    ));
    return src.then((call) => {
      if (!call.response) {
        throw new Error("APIPromise.asResponse: response unavailable");
      }
      return call.response;
    });
  }

  async withResponse(): Promise<{ data: T; response: Response }> {
    const [[data], response] = await Promise.all([
      this._promise,
      this.asResponse(),
    ]);
    return { data, response };
  }

  _thenUnwrap<U>(transform: (value: T) => U): APIPromise<U> {
    const data = this._promise.then(
      ([value, call]): [U, APICall] => [transform(value), call],
    );
    data.catch(() => {});
    return new APIPromise<U>(data, this._callSource ?? undefined);
  }
}

export function unwrapAsAPIPromise<T>(
  p: APIPromise<Result<T, unknown>>,
): APIPromise<T> {
  const inner = p.$inspect();
  const data: Promise<[T, APICall]> = inner.then(([r, call]) => {
    if (!r.ok) {
      throw r.error;
    }
    return [r.value, call];
  });
  const callSource: Promise<APICall> = inner.then(([r, call]) => {
    if (!r.ok && !call.response?.ok) {
      throw r.error;
    }
    return call;
  });
  data.catch(() => {});
  callSource.catch(() => {});
  return new APIPromise<T>(data, callSource);
}
